import React, { useEffect, useRef, useState } from "react";
import "./index.css";

import Overlay from "../Overlay";
import DropPinGame from "../../pages/InitialSetup/components/DropPinGame";

const STAGES = [
    "Reading your CV...",
    "Pulling out your experience...",
    "Picking up qualifications and certs...",
    "Almost done...",
];

const EXPECTED_SECONDS = 30;

export default ({ show, applying, profileReady, onApply, onSkip, onContinue }) => {

    const [elapsed, setElapsed] = useState(0);
    const [gameStarted, setGameStarted] = useState(false);
    const startRef = useRef(null);

    useEffect(() => {
        if (!applying) {
            setElapsed(0);
            startRef.current = null;
            if (!profileReady) setGameStarted(false);
            return;
        }
        startRef.current = Date.now();
        const interval = setInterval(() => {
            setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
        }, 250);
        return () => clearInterval(interval);
    }, [applying, profileReady]);

    if (!show) return null;

    const inProgress = applying || profileReady;
    const inGame = inProgress && gameStarted;
    const stageIdx = Math.min(STAGES.length - 1, Math.floor(elapsed / 8));
    const rawPct = Math.min(1, elapsed / EXPECTED_SECONDS);
    const easedPct = 1 - Math.pow(1 - rawPct, 1.6);
    const progress = profileReady ? 100 : Math.min(95, easedPct * 100);

    return (
        <Overlay>
            <div className="cv-repopulate-modal-positioner">
                <div
                    className={"cv-repopulate-modal" + (gameStarted ? " cv-repopulate-modal--wide" : "")}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="cv-repopulate-title"
                >
                    {!inProgress ? (
                        <>
                            <h2 className="cv-repopulate-title" id="cv-repopulate-title">Update your profile from this CV?</h2>
                            <p className="cv-repopulate-body">
                                We'll read your CV and prefill your experience, qualifications, and personal details. You can edit anything afterwards.
                            </p>
                            <p className="cv-repopulate-hint">Usually takes about 30 seconds.</p>
                            <div className="cv-repopulate-actions">
                                <button type="button" className="cv-repopulate-button cv-repopulate-button--secondary" onClick={onSkip}>Skip</button>
                                <button type="button" className="cv-repopulate-button cv-repopulate-button--primary" onClick={onApply}>Apply to profile</button>
                            </div>
                        </>
                    ) : (
                        <>
                            <h2 className="cv-repopulate-title" id="cv-repopulate-title">
                                {profileReady ? "Your profile is ready" : "Reading your CV"}
                            </h2>
                            <p className="cv-repopulate-status" key={profileReady ? "ready" : stageIdx}>
                                {profileReady
                                    ? (gameStarted ? "Continue when you're ready, or keep playing." : "Click Continue when you're ready.")
                                    : STAGES[stageIdx]}
                            </p>
                            <div
                                className={"cv-repopulate-progress" + (profileReady ? " cv-repopulate-progress--complete" : "")}
                                role="progressbar"
                                aria-valuenow={Math.floor(progress)}
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-label="CV processing progress"
                            >
                                <div className="cv-repopulate-progress-fill" style={{width: `${progress}%`}} />
                            </div>
                            {profileReady ? (
                                <button type="button" className="cv-repopulate-continue" onClick={onContinue}>
                                    See your updated CV
                                    <span className="cv-repopulate-continue-arrow">→</span>
                                </button>
                            ) : (
                                <p className="cv-repopulate-elapsed">
                                    <span className="cv-repopulate-elapsed-seconds">{elapsed}s</span>
                                    <span className="cv-repopulate-elapsed-divider"> · </span>
                                    <span className="cv-repopulate-elapsed-expected">usually 25–35s</span>
                                </p>
                            )}
                            {!gameStarted && !profileReady && (
                                <button
                                    type="button"
                                    className="cv-repopulate-play-game"
                                    onClick={() => setGameStarted(true)}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M9 4L3 6V20L9 18M9 4L15 6M9 4V18M15 6L21 4V18L15 20M15 6V20M9 18L15 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Play a game while you wait?
                                </button>
                            )}
                            {inGame && (
                                <DropPinGame
                                    profileReady={profileReady}
                                    onContinue={onContinue}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </Overlay>
    );

}
